import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { approvalsApi } from '@/lib/api/approvals'
import type { PaymentListItem } from '@/lib/api/payments'

interface OTPModalProps {
  payment: PaymentListItem | null
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const formatAmount = (paise: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(paise / 100)

export default function OTPModal({ payment, open, onClose, onSuccess }: OTPModalProps) {
  const [step, setStep] = useState<'request' | 'enter'>('request')
  const [otp, setOtp] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const requestOTPMutation = useMutation({
    mutationFn: () => approvalsApi.requestOTP(payment!.id),
    onSuccess: () => {
      setStep('enter')
      setErrorMsg('')
    },
    onError: (err: any) => {
      setErrorMsg(err?.error ?? 'Failed to send OTP. Please try again.')
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => approvalsApi.approve(payment!.id, otp),
    onSuccess: () => {
      onSuccess()
      handleClose()
    },
    onError: (err: any) => {
      setErrorMsg(err?.error ?? 'Approval failed. Please check the OTP and try again.')
    },
  })

  function handleClose() {
    setStep('request')
    setOtp('')
    setErrorMsg('')
    onClose()
  }

  if (!payment) return null

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Payment</DialogTitle>
          <DialogDescription>
            {formatAmount(payment.amount)} to <span className="font-medium">{payment.payeeName}</span> via {payment.paymentMode}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {errorMsg && (
            <Alert variant="destructive">
              <p className="text-sm">{errorMsg}</p>
            </Alert>
          )}

          {step === 'request' ? (
            <p className="text-sm text-muted-foreground">
              Click <strong>Send OTP</strong> to receive a one-time password on the registered ICICI corporate mobile number.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit OTP sent to your registered corporate mobile number.
              </p>
              <div className="space-y-1">
                <Label htmlFor="otp-input">One-Time Password</Label>
                <Input
                  id="otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-lg tracking-widest"
                  autoFocus
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={requestOTPMutation.isPending || approveMutation.isPending}>
            Cancel
          </Button>

          {step === 'request' ? (
            <Button
              onClick={() => requestOTPMutation.mutate()}
              disabled={requestOTPMutation.isPending}
            >
              {requestOTPMutation.isPending ? 'Sending…' : 'Send OTP'}
            </Button>
          ) : (
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={otp.length !== 6 || approveMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {approveMutation.isPending ? 'Approving…' : 'Confirm Approval'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
